/**
 * One-time миграция auth-state из легаси `showgo-office` в v3.
 *
 * Задача: пользователь, у которого валидная сессия в старой админке
 * (cabinet.kassir.kg) на том же домене, при первом заходе в v3 не должен
 * повторно логиниться. Поднимаем его состояние из легаси-ключей
 * localStorage и раскладываем по нашим.
 *
 * Легаси-ключи (см. `showgo-office/src/atoms/auth/*.ts`):
 *   • `user`        — JSON `{token, subToken, user: {...}, isInvitationsEnabled}`
 *   • `permission`  — JSON выбранного Permission (тот же shape, что у нас)
 *
 * V3-ключи (см. `services/utils/auth-storage.ts`):
 *   • `kassir:auth:token`       — активный токен (scoped после picker'а)
 *   • `kassir:auth:user`        — user-объект
 *   • `kassir:auth:permission`  — выбранный permission
 *
 * Правила:
 *   1. Если в v3 уже есть свой токен — ничего не делаем (уже мигрировано
 *      либо свежее v3-логин).
 *   2. Если в легаси нет `'user'` или парсинг падает — ничего не делаем.
 *   3. Активный токен = `subToken` (scoped, если контекст выбран) иначе
 *      обычный `token`. Совпадает с логикой легаси-fetch'а.
 *   4. Легаси-ключи оставляем как есть — старая админка может ещё
 *      работать параллельно во время переходного периода.
 *
 * Вызывается синхронно из `main.tsx` до `createRoot`, чтобы
 * `atomWithStorage({getOnInit: true})` при первом чтении увидел
 * уже мигрированные значения.
 */

import {PERMISSION_STORAGE_KEY, TOKEN_STORAGE_KEY, USER_STORAGE_KEY} from './auth-storage';

const LEGACY_USER_KEY = 'user';
const LEGACY_PERMISSION_KEY = 'permission';

type LegacyUserPayload = {
  token?: string;
  subToken?: string;
  user?: unknown;
  isInvitationsEnabled?: boolean;
};

const safeParse = <T>(raw: string | null): T | null => {
  if (raw == null || raw === '') return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

/** Записать значение под ключ `atomWithStorage`-совместимо (через JSON). */
const writeJson = (key: string, value: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // Приватный режим / переполнение — не критично, миграция
    // просто не сработает, пользователь пойдёт на /login.
    if (import.meta.env.DEV) {
      console.warn('[legacyAuthMigrate] failed to write', key, e);
    }
  }
};

export const migrateLegacyAuthIfNeeded = (): void => {
  // 1. Свои ключи уже заполнены — не трогаем.
  if (localStorage.getItem(TOKEN_STORAGE_KEY) != null) return;

  // 2. В легаси нет юзера — не трогаем.
  const legacyUser = safeParse<LegacyUserPayload>(localStorage.getItem(LEGACY_USER_KEY));
  if (!legacyUser) return;

  // 3. Определяем активный токен. `subToken` — scoped (после picker'а),
  //    `token` — general (сразу после login). Берём то, что есть.
  const activeToken = legacyUser.subToken || legacyUser.token;
  if (!activeToken || typeof activeToken !== 'string') return;

  // 4. Пишем токен + user. `user` в легаси лежит вложенно.
  writeJson(TOKEN_STORAGE_KEY, activeToken);
  if (legacyUser.user && typeof legacyUser.user === 'object') {
    writeJson(USER_STORAGE_KEY, legacyUser.user);
  }

  // 5. Permission — если выбран, переносим. Иначе `selectedPermissionAtom`
  //    останется null и v3 покажет ContextPicker — тоже валидный сценарий.
  const legacyPermission = safeParse<unknown>(localStorage.getItem(LEGACY_PERMISSION_KEY));
  if (legacyPermission && typeof legacyPermission === 'object') {
    writeJson(PERMISSION_STORAGE_KEY, legacyPermission);
  }

  if (import.meta.env.DEV) {
    console.info('[legacyAuthMigrate] migrated from legacy showgo-office keys');
  }
};
